import { useState } from 'react';
import { Mail, Plus } from 'lucide-react';
import { extractErrorMessage } from '../../api/errors';
import { Badge, Button, Callout, Card, CardContent, Spinner } from '../../components/ui';
import { useBrand } from '../../contexts/BrandContext';
import { EmailSeries, SeriesTrigger, useCreateEmailSeries, useEmailSeriesList } from '../../queries/emailSeries';
import { EmailSeriesDetail } from './EmailSeriesDetail';
import { STATUS_LABELS, STATUS_VARIANTS, canWriteMarketing, describeOffer } from './emailSeriesFormat';

// Marketing -> Series: automated emails that follow a trigger such as a trial
// ending, each carrying a personal discount code. The list shows how each
// series is doing; opening one edits its offer, timing and steps.

const STAT_LABELS: [keyof EmailSeries['stats'], string][] = [
  ['enrolled', 'Enrolled'],
  ['sent', 'Sent'],
  ['codesIssued', 'Codes issued'],
  ['redeemed', 'Redeemed'],
  ['converted', 'Converted'],
  ['unsubscribed', 'Unsubscribed'],
];

export function EmailSeriesPage() {
  const { role } = useBrand();
  const canWrite = canWriteMarketing(role);
  const { data, isLoading, error } = useEmailSeriesList();
  const [openId, setOpenId] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);

  if (isLoading) return <Spinner />;
  if (error) return <Callout variant="error" title="Couldn't load series">{extractErrorMessage(error)}</Callout>;

  const triggers = data?.triggers ?? [];
  const variables = data?.variables ?? [];
  const triggerText = (type: string) => triggers.find((t) => t.type === type)?.description ?? type;

  if (openId !== null) {
    return (
      <EmailSeriesDetail
        seriesId={openId}
        triggerDescription={triggerText(data?.series.find((s) => s.id === openId)?.triggerType ?? '')}
        variables={variables}
        canWrite={canWrite}
        onBack={() => setOpenId(null)}
      />
    );
  }

  const series = data?.series ?? [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          Emails sent automatically after something happens to a customer, such as a trial ending. Each step goes out a set number of days later.
        </p>
        {canWrite && (
          <Button size="sm" onClick={() => setCreating(true)}>
            <Plus />
            New series
          </Button>
        )}
      </div>

      {creating && <NewSeriesForm triggers={triggers} onDone={(id) => { setCreating(false); if (id) setOpenId(id); }} />}

      {series.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">No series yet.</CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {series.map((s) => (
            <Card key={s.id} data-testid={`series-${s.id}`}>
              <CardContent className="space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <h3 className="truncate font-semibold">{s.name}</h3>
                      <Badge variant={STATUS_VARIANTS[s.status]}>{STATUS_LABELS[s.status]}</Badge>
                    </div>
                    <p className="mt-1 text-sm text-muted-foreground">{triggerText(s.triggerType)}</p>
                    <p className="mt-1 text-sm">{describeOffer(s.discountLabel, s.codeValidDays)}</p>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => setOpenId(s.id)}>
                    Open
                  </Button>
                </div>
                <dl className="grid grid-cols-3 gap-2 sm:grid-cols-6">
                  {STAT_LABELS.map(([key, label]) => (
                    <div key={key} className="rounded-md bg-muted/50 px-2 py-1.5">
                      <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</dt>
                      <dd className="text-sm font-semibold" data-testid={`series-${s.id}-${key}`}>{(s.stats?.[key] ?? 0).toLocaleString('en-AU')}</dd>
                    </div>
                  ))}
                </dl>
                {s.stats?.dryRun > 0 && (
                  <p className="text-xs text-muted-foreground">{s.stats.dryRun.toLocaleString('en-AU')} dry-run emails recorded (not sent).</p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function NewSeriesForm({ triggers, onDone }: { triggers: SeriesTrigger[]; onDone: (createdId?: number) => void }) {
  const create = useCreateEmailSeries();
  const [name, setName] = useState('');
  const [triggerType, setTriggerType] = useState(triggers[0]?.type ?? '');
  const [error, setError] = useState<string | null>(null);

  const submit = () => {
    setError(null);
    create.mutate(
      { name: name.trim(), triggerType },
      { onSuccess: (series) => onDone(series.id), onError: (err) => setError(extractErrorMessage(err, 'Could not create the series.')) },
    );
  };

  return (
    <Card>
      <CardContent className="space-y-3">
        <h3 className="text-sm font-semibold">New series</h3>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor="new-series-name" className="mb-1 block text-sm font-medium">Name</label>
            <input
              id="new-series-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm"
            />
          </div>
          <div>
            <label htmlFor="new-series-trigger" className="mb-1 block text-sm font-medium">Starts when</label>
            <select
              id="new-series-trigger"
              value={triggerType}
              onChange={(e) => setTriggerType(e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm"
            >
              {triggers.map((t) => (
                <option key={t.type} value={t.type}>{t.description}</option>
              ))}
            </select>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">New series start paused, with no steps.</p>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={() => onDone()}>Cancel</Button>
          <Button size="sm" onClick={submit} disabled={!name.trim() || !triggerType || create.isPending}>
            Create series
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
